<template>
    <footer id="footer" class="home-footer">
        <div class="footer-top">
            <div class="container">
                <nav class="footer-menu" aria-label="Footer">
                    <router-link to="/policy/terms">{{ t('footer.terms_of_use') }}</router-link>
                    <router-link to="/policy/privacy" class="is-strong">{{ t('footer.privacy_policy') }}</router-link>
                    <a href="/sitemap">{{ t('footer.site_map') }}</a>
                </nav>
            </div>
        </div>

        <div class="container footer-layout" :class="{ 'has-info': showInfoCards }">
            <section class="footer-col footer-company">
                <h3>CHINAONE</h3>
                <div class="company-lines footer-rich-content" v-html="companyContent"></div>
            </section>

            <template v-if="showInfoCards">
                <section class="footer-col rate-card">
                    <h3>{{ t('mny.title') }} (CNY)</h3>
                    <div class="rate-row"><span>{{ t('mny.exg_rt') }}</span><strong>{{
                        formatRate(chinaRate.applyExchangeRate) }}</strong></div>
                    <div class="rate-row"><span>{{ t('mny.appl_exg_rt') }}</span><strong>{{
                        formatRate(chinaRate.exchangeRate) }}</strong></div>
                    <div class="rate-row"><span>{{ ttRate.rateTypeName || t('mny.tt') }}</span><strong>{{
                        formatRate(ttRate.rateValue, 4) }}</strong></div>
                    <p class="period">{{ t('mny.sbj') }} {{ exchangeRatePeriod }}</p>
                </section>

                <section class="footer-col bank-card">
                    <h3>{{ t('bank_column.title') }}</h3>
                    <p class="bank-name">{{ bank.bankName || 'KB국민은행' }}</p>
                    <strong class="bank-number">{{ bank.cardNumber || '-' }}</strong>
                    <p class="bank-holder">{{ t('bank_column.acc_holder') }}: {{ bank.cardHolderName || '-' }}</p>
                    <p class="card-note">{{ t('bank_column.msg1') }} {{ t('bank_column.msg2') }}</p>
                </section>

                <section class="footer-col service-card">
                    <h3>{{ t('menu.customer_service') }}</h3>
                    <template v-if="customerServiceSections">
                        <p v-for="(item, index) in customerServiceSections.phoneLines" :key="index"
                            class="service-row">
                            <span>{{ item.label }}</span><strong>{{ item.phone }}</strong>
                        </p>
                        <div class="service-hours">
                            <template v-for="(item, index) in customerServiceSections.hoursLines" :key="index">
                                <span class="hours-label">{{ item.label }}</span><span class="hours-time">{{ item.time }}</span>
                            </template>
                        </div>
                    </template>
                    <div v-else-if="customerServiceContent" class="service-config-content footer-rich-content"
                        v-html="customerServiceContent"></div>
                </section>
            </template>
        </div>
    </footer>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { depositService } from '@/services/userwallet/deposit'
import { USDTTSystemExchangeRateControllerApi } from '@/http/apis/USDTTSystemExchangeRateControllerApi'
import { WebConfigAppServiceApi } from '@/http/apis/WebConfigAppServiceApi'

defineProps({
    footerText: { type: String, default: '' },
    showInfoCards: { type: Boolean, default: true },
})

const { t, locale } = useI18n()
const exchangeRateApi = new USDTTSystemExchangeRateControllerApi()
const webConfigApi = new WebConfigAppServiceApi()
const chinaRate = ref({})
const ttRate = ref({})
const bank = ref({ bankName: '', cardNumber: '', cardHolderName: '' })
const companyConfig = ref(null)
const customerServiceConfig = ref(null)

const normalizeFooterHtml = (content) => {
    if (!content || typeof document === 'undefined') return content || ''

    const container = document.createElement('div')
    container.innerHTML = content
    container.querySelectorAll('*').forEach((element) => {
        if (!(element instanceof HTMLElement)) return
        element.removeAttribute('width')
        element.style.removeProperty('width')
        element.style.removeProperty('min-width')
        element.style.setProperty('max-width', '100%', 'important')
        element.style.setProperty('overflow-wrap', 'anywhere', 'important')
        element.style.setProperty('word-break', 'break-word', 'important')
        element.style.setProperty(
            'white-space',
            element.matches('pre, pre *, code, code *') ? 'pre-wrap' : 'normal',
            'important'
        )
    })
    return container.innerHTML
}

const getLocalizedContent = (config) => {
    if (!config) return ''
    const content = locale.value.startsWith('ko')
        ? (config.content || config.contentCn)
        : (config.contentCn || config.content)
    return normalizeFooterHtml(content)
}

const companyContent = computed(() => getLocalizedContent(companyConfig.value))
const customerServiceContent = computed(() => getLocalizedContent(customerServiceConfig.value))

const extractRichTextLines = (content) => {
    if (!content || typeof document === 'undefined') return []

    const container = document.createElement('div')
    container.innerHTML = content
    container.querySelectorAll('br').forEach((element) => element.replaceWith('\n'))
    container.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6').forEach((element) => {
        element.append(document.createTextNode('\n'))
    })
    return (container.textContent || '')
        .split(/\r?\n/)
        .map((line) => line.replace(/\s+/g, ' ').trim())
        .filter(Boolean)
}

const splitPhoneLine = (line) => {
    const match = line.match(/((?:\d{2,4}[- ]?){2}\d{3,4})\s*$/)
    if (!match) return { label: line, phone: '' }
    return {
        label: line.slice(0, match.index).trim(),
        phone: match[1],
    }
}

// "월~금요일 10:00 ~ 17:30" → 标签 + 时间，两列对齐显示
const splitHoursLine = (line) => {
    const index = line.search(/\d{1,2}:\d{2}/)
    if (index <= 0) return { label: '', time: line }
    return { label: line.slice(0, index).trim(), time: line.slice(index).trim() }
}

const customerServiceSections = computed(() => {
    const lines = extractRichTextLines(customerServiceContent.value)
    const firstHoursLineIndex = lines.findIndex((line) => /\d{1,2}:\d{2}/.test(line))
    const hoursTitleIndex = firstHoursLineIndex - 1
    if (lines.length < 4 || firstHoursLineIndex < 2 || hoursTitleIndex <= 0) return null

    return {
        phoneLines: lines.slice(1, hoursTitleIndex).map(splitPhoneLine),
        hoursLines: lines.slice(firstHoursLineIndex).map(splitHoursLine),
    }
})

const exchangeRatePeriod = computed(() => {
    const startDate = chinaRate.value.startDate || '-'
    const endDate = chinaRate.value.endDate || '-'
    return `${startDate} ~ ${endDate}`
})

const formatRate = (value, minimumFractionDigits = 0) => {
    const numericValue = Number(value)
    if (!Number.isFinite(numericValue)) return '-'
    return numericValue.toLocaleString(undefined, { minimumFractionDigits, maximumFractionDigits: 4 })
}

const fetchExchangeRate = async () => {
    try {
        const res = await exchangeRateApi.systemExchangeRateEchoGet()
        chinaRate.value = res.data?.chinaRate || {}
        ttRate.value = res.data?.ttRate || {}
    } catch (error) {
        console.error('获取底部汇率信息失败', error)
    }
}

const fetchBank = async () => {
    try {
        const data = await depositService.getMbtBankAccount()
        if (data) bank.value = {
            bankName: data.bankName || '',
            cardNumber: data.cardNumber || '',
            cardHolderName: data.cardHolderName || '',
        }
    } catch (error) {
        console.error('获取底部汇款账户失败', error)
    }
}

const fetchFooterConfig = async () => {
    try {
        const [companyRes, customerServiceRes] = await Promise.all([
            webConfigApi.webConfigPageListPageTypeGet({ pageType: 3 }),
            webConfigApi.webConfigPageListPageTypeGet({ pageType: 4 }),
        ])
        companyConfig.value = companyRes.data?.[0] || null
        customerServiceConfig.value = customerServiceRes.data?.[0] || null
    } catch (error) {
        console.error('获取底部公司及客服信息失败', error)
    }
}

onMounted(() => {
    Promise.all([
        fetchBank(),
        fetchExchangeRate(),
        fetchFooterConfig(),
    ])
})
</script>


<style scoped>
/* 浅灰底部：顶部链接行 + 四列信息（公司 / 汇率 / 账户 / 客服） */
#footer.home-footer {
    box-sizing: border-box;
    padding: 0 0 26px;
    border-top: 1px solid #e2e6ec;
    background: #f1f3f6;
    color: #5b6676;
    font-size: 13px;
    line-height: 1.75;
}

.footer-top {
    border-bottom: 1px solid #e2e6ec;
}

.footer-menu {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    padding: 14px 0;
}

.footer-menu a {
    color: #5b6676;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
}

.footer-menu a.is-strong {
    color: #1f2937;
    font-weight: 700;
}

.footer-menu a:hover {
    color: #1f2937;
}

.footer-layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: 40px;
    padding-top: 22px;
}

.footer-layout.has-info {
    grid-template-columns: minmax(0, 1.2fr) minmax(0, .9fr) minmax(0, .9fr) minmax(0, 1.1fr);
}

.footer-col {
    min-width: 0;
}

.footer-col h3 {
    margin: 0 0 16px;
    color: #1f2937;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.4;
}

.footer-company h3 {
    font-size: 17px;
}

.company-lines {
    color: #5b6676;
    font-size: 13px;
}

.company-lines :deep(*) {
    color: inherit !important;
    font-size: inherit !important;
    font-weight: 400 !important;
    background: transparent !important;
}

.footer-rich-content {
    max-width: 100%;
    overflow: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.footer-rich-content :deep(*) {
    box-sizing: border-box;
    max-width: 100% !important;
    color: inherit !important;
    overflow-wrap: anywhere !important;
    word-break: break-word !important;
}

.footer-rich-content :deep(p) {
    margin: 1px 0;
}

.footer-rich-content :deep(img) {
    height: auto;
}

.rate-row,
.service-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 4px;
}

.rate-row strong,
.service-row strong {
    color: #1f2937;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

.period {
    margin: 12px 0 0;
    color: #8a94a3;
    font-size: 12px;
}

.bank-card p {
    margin: 0;
}

.bank-number {
    display: block;
    color: #1f2937;
    font-size: 15px;
    font-weight: 700;
    overflow-wrap: anywhere;
}

.card-note {
    margin-top: 14px !important;
    color: #8a94a3;
    font-size: 12px;
    line-height: 1.6;
}

.service-hours {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e2e6ec;
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: 14px;
}

.service-hours .hours-time {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.service-config-content {
    color: #5b6676;
}

@media (max-width: 1200px) {
    .footer-layout.has-info {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 32px;
    }
}

@media (max-width: 700px) {
    #footer.home-footer {
        padding-bottom: 28px;
    }

    .footer-layout,
    .footer-layout.has-info {
        grid-template-columns: 1fr;
        gap: 24px;
        padding-top: 18px;
    }

    .footer-menu {
        gap: 16px;
    }
}
</style>
